import { useQueryClient } from '@tanstack/react-query'
import { createRestClient } from '@/contexts/QueryProvider'
import { ErrorComponent } from '../ErrorComponent'
import { toast } from 'react-toastify'
import { Button } from '@fluentui/react-components'
import { SkeletonItem } from '@fluentui/react-components'
import { Add24Regular, FolderList20Regular, FolderOpen24Regular, Notebook24Regular } from '@fluentui/react-icons'
import { useState } from 'react'

type DatabricksPanelProps = {
  appId: number
}

const statusIcon = (status?: string) => {
  if (status === 'Check') {
    return <span className="text-green-500">✔</span>
  } else if (status === 'Warn') {
    return <span className="text-yellow-500">⚠</span>
  } else if (status === 'Problem') {
    return <span className="text-red-500">✖</span>
  } else return <span className="text-light">{status}</span>
}

export const DatabricksPanel = ({ appId }: DatabricksPanelProps) => {
  const [busy, setBusy] = useState<boolean>()
  const queryClient = useQueryClient()
  const {
    getDatabricksApiGetDatabricksStatus,
    getDatabricksApiGetDatabricksApplicationStatus,
    putDatabricksApiProvisionApplication,
    postDatabricksApiPromoteApplication,
    putDatabricksApiInitialiseDatabricks,
    putDatabricksApiAddDefaultApplicationNotebook,
  } = createRestClient()
  const {
    data: connectionStatus,
    isPending: isConnectionPending,
    isError: isConnectionError,
  } = getDatabricksApiGetDatabricksStatus.useQuery(undefined, {})

  //const invalidateConnection = () => getDatabricksApiGetDatabricksStatus.invalidateQueries(queryClient)

  const {
    data: applicationStatus,
    isPending: isApplicationPending,
    isError: isApplicationError,
  } = getDatabricksApiGetDatabricksApplicationStatus.useQuery(
    { query: { applicationId: appId } },
    {
      enabled: !!appId,
    }
  )

  const invalidateApplication = () => getDatabricksApiGetDatabricksApplicationStatus.invalidateQueries(queryClient)

  const { mutateAsync: initialiseDatabricks } = putDatabricksApiInitialiseDatabricks.useMutation()
  const { mutateAsync: provisionApp } = putDatabricksApiProvisionApplication.useMutation()
  const { mutateAsync: promoteApp } = postDatabricksApiPromoteApplication.useMutation()
  const { mutateAsync: addDefaultNotebook } = putDatabricksApiAddDefaultApplicationNotebook.useMutation()

  const onInitialise = () => {
    setBusy(true)
    toast.info('Please wait while Databricks is initialised')
    initialiseDatabricks(
      {},
      {
        onError: (error) => {
          toast.error('Error initialising Databricks: ' + error)
        },
        onSuccess: () => {
          toast.success('Databricks initialisation complete')
        },
        onSettled: () => {
          invalidateApplication()
          setBusy(false)
        },
      }
    )
  }

  const onProvisionApp = () => {
    setBusy(true)
    toast.info('Please wait while the application is provisioned on Databricks')
    provisionApp(
      { query: { applicationId: appId } },
      {
        onError: (error) => {
          toast.error('Error provisioning app: ' + error)
        },
        onSuccess: () => {
          toast.success('App provisioning complete')
        },
        onSettled: () => {
          invalidateApplication()
          setBusy(false)
        },
      }
    )
  }

  const onPromoteApp = () => {
    setBusy(true)
    toast.info('Please wait while the application is promoted on Databricks')
    promoteApp(
      { query: { applicationId: appId } },
      {
        onError: (error) => {
          toast.error('Error promoting app: ' + error)
        },
        onSuccess: () => {
          toast.success('App promoting complete. You may need to Sync workspaces')
        },
        onSettled: () => {
          invalidateApplication()
          setBusy(false)
        },
      }
    )
  }

  const onAddNotebook = () => {
    setBusy(true)
    toast.info('Please wait while a new notebook is added')
    addDefaultNotebook(
      { query: { applicationId: appId } },
      {
        onError: (error) => {
          toast.error('Error adding notebook to app: ' + error)
        },
        onSuccess: () => {
          toast.success('Added notebook successfully')
        },
        onSettled: () => {
          invalidateApplication()
          setBusy(false)
        },
      }
    )
  }

  if (isConnectionError || isApplicationError || !appId) return <ErrorComponent />

  const skeletonContent = (
    <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
      {new Array(6).fill(0).map((_, i) => (
        <div className="flex flex-col whitespace-nowrap" key={i}>
          <span className="text-sm font-light">
            <SkeletonItem size={16} className="width-200" style={{ width: '100px' }} />
          </span>
          <span className="text-base">
            <SkeletonItem shape="circle" size={24} />
          </span>
        </div>
      ))}
    </div>
  )

  let connectionContent
  if (isConnectionPending) {
    connectionContent = skeletonContent
  } else {
    const { items: connectionStatusItems } = connectionStatus
    connectionContent = (
      <>
        {connectionStatusItems!.map(({ name, icon, messages }) => (
          <div className="flex flex-col whitespace-nowrap" key={name}>
            <span className="text-sm font-light">{name}:</span>
            <span className="text-base">
              {statusIcon(icon)} <span className="font-light">{messages}</span>
            </span>
          </div>
        ))}
      </>
    )
  }

  let applicationContent
  let notebookContent
  if (isApplicationPending) {
    applicationContent = skeletonContent
    notebookContent = skeletonContent
  } else {
    const { developmentItems, productionItems, notebooks, devFolderUrl, prodFolderUrl } = applicationStatus
    applicationContent = (
      <>
        <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
          {developmentItems!.map(({ name, icon, messages }) => (
            <div className="flex flex-col whitespace-nowrap" key={name}>
              <span className="text-sm font-light">{name}:</span>
              <span className="text-base">
                {statusIcon(icon)} <span className="font-light">{messages}</span>
              </span>
            </div>
          ))}
        </div>
        <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
          {productionItems!.map(({ name, icon, messages }) => (
            <div className="flex flex-col whitespace-nowrap" key={name}>
              <span className="text-sm font-light">{name}:</span>
              <span className="text-base">
                {statusIcon(icon)} <span className="font-light">{messages}</span>
              </span>
            </div>
          ))}
        </div>
      </>
    )

    notebookContent = (
      <>
        <div className="grid grid-cols-1 gap-4 py-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {notebooks!.map(({ notebookPath, language, objectId, size, notebookUrl }) => (
            <div className="grid grid-cols-[1fr_4fr_1fr] gap-2 rounded-lg bg-white p-4 shadow">
              <div className="flex h-12 w-12 flex-shrink-0 flex-grow-0 items-center justify-center self-start rounded-full bg-grey-lightest p-2 text-grey-dark">
                <Notebook24Regular />
              </div>
              <div className="flex flex-col justify-between gap-1">
                <div className="text-base">{notebookPath}</div>
                <div className="text-xs font-light">{language}</div>
              </div>
              <div className="col-span-2 col-start-2 flex flex-row justify-end gap-2 border-t pt-3">
                <Button
                  appearance="primary"
                  size="small"
                  icon={<Notebook24Regular />}
                  onClick={() => window.open(notebookUrl, '_blank')}
                >
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      </>
    )

    return (
      <>
        <div className="flex flex-col gap-y-5 py-4">
          <div className="w-full space-y-4">
            <div className="flex w-full flex-col gap-y-4 rounded bg-white p-6 pt-4">
              <div className="flex flex-row items-center justify-between border-b">
                <h6>Databricks Connection / Configuration</h6>
              </div>
              <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
                {connectionContent}
                <div className="flex flex-grow flex-row gap-2 whitespace-nowrap">
                  <span className="flex-grow"></span>
                  <span className="flex flex-shrink flex-col gap-2 text-base">
                    <Button
                      disabled={busy}
                      appearance={connectionStatus?.items.some((x) => x.icon !== 'Check') ? 'primary' : 'secondary'}
                      onClick={() => onInitialise()}
                    >
                      Initialise Databricks
                    </Button>
                    <span className="text-sm font-light">Create necessary elements in Databricks</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-y-5 py-4">
          <div className="w-full space-y-4">
            <div className="flex w-full flex-col gap-y-4 rounded bg-white p-6 pt-4">
              <div className="flex flex-row items-center justify-between border-b">
                <h6>Databricks Application Checks</h6>
              </div>
              {applicationContent}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-y-5 py-4">
          <div className="w-full space-y-4">
            <div className="flex w-full flex-col gap-y-4 rounded bg-white p-6 pt-4">
              <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
                <div className="flex flex-col gap-2 whitespace-nowrap">
                  <span className="text-sm">Provision App:</span>
                  <span className="flex flex-col gap-2 text-base">
                    <Button
                      disabled={busy}
                      appearance={
                        applicationStatus.developmentItems?.some((x) => x.icon !== 'Check') ? 'primary' : 'secondary'
                      }
                      onClick={() => onProvisionApp()}
                    >
                      Provision App
                    </Button>
                    <span className="text-sm font-light">Create application folders and set up permissions</span>
                  </span>
                </div>
                <div className="flex flex-col gap-2 whitespace-nowrap">
                  <span className="text-sm">Promote App:</span>
                  <span className="flex flex-col gap-2 text-base">
                    <Button
                      disabled={busy}
                      appearance={
                        applicationStatus.productionItems?.some((x) => x.icon !== 'Check') ? 'primary' : 'secondary'
                      }
                      onClick={() => onPromoteApp()}
                    >
                      Promote App
                    </Button>
                    <span className="text-sm font-light">Copy development folder to production</span>
                  </span>
                </div>

                <div className="grid grid-cols-[1fr_4fr] gap-4 rounded-lg bg-white p-4 shadow">
                  <div className="flex h-12 w-12 flex-shrink-0 flex-grow-0 items-center justify-center self-start rounded-full bg-grey-lightest p-2 text-grey-dark">
                    <FolderList20Regular fontSize={24} />
                  </div>
                  <div className="flex flex-col justify-between gap-1">
                    <Button
                      size="small"
                      icon={<FolderOpen24Regular />}
                      onClick={() => window.open(devFolderUrl, '_blank')}
                      disabled={devFolderUrl === ''}
                    >
                      Development
                    </Button>
                    <Button
                      size="small"
                      icon={<FolderOpen24Regular />}
                      onClick={() => window.open(prodFolderUrl, '_blank')}
                      disabled={prodFolderUrl === ''}
                    >
                      Production
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-b pb-1">
          <div>
            <h5>Notebooks</h5>
            <p>Manage the notebooks for your application below.</p>
          </div>
          <Button
            disabled={busy}
            appearance={applicationStatus.notebooks.length == 0 ? 'primary' : 'transparent'}
            icon={<Add24Regular />}
            onClick={() => onAddNotebook()}
          />
        </div>

        {notebookContent}
      </>
    )
  }
}
